import React, { useEffect, useMemo, useState } from 'react'
import { Box, Chip, Dialog, FormHelperText, Grid, SvgIcon, Typography } from '@material-ui/core'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Form, Formik } from 'formik'
import { PhoneField, TextField } from '~/components/fields'
import { composeT, email, maxLength, required } from '~/common/validator'
import { Button } from '~/components/button'
import { appStore } from '~/stores'
import { observer } from 'mobx-react-lite'
import { CloseIconDialog } from '~/components/icons'
import { quickApplyModalStyled } from '~/features/quick-apply/quick-apply-component.style'
import { careStore } from '~/companies/care/care.store'
import { FIELD_MODE } from '~/common/constants'
import { useClipClasses } from '~/components/fields/autocomplete-field/autocomplete-field.style'
import { careJobDetailStore } from '~/features/care-jobs/detail/care-job-detail.store'
import { labelStyle } from '~/components/apply-job-dialog/apply-job-dialog.style'
import { mapDisciplineSpecialty } from '~/components/apply-job-dialog/apply-job-dialog.helper'
import { UpdateApplyJobDialog } from '~/components/apply-job-dialog/update-apply-job-dialog'
import { infoTabStore } from '~/features/care-activation/tabs/info/info-tab.store'
import { useEffectOnce } from 'react-use'
import { includes, isEmpty, isNil, some, uniq } from 'lodash'
import { NotifyDialog } from '~/components/notify'
import { quickApplyStore } from '~/features/quick-apply/quick-apply.store'
import { additionalInfoApplyDialogStore } from '~/components/additional-info-apply-dialog/additional-info-apply-dialog.store'
import { careProfileStore } from '~/features/care-profile/care-profile.store'
import { referenceTabStore } from '~/components/additional-info-apply-dialog/tabs/reference/reference-tab.store'
import { logClickEvent, logTrackingScreenEvent } from '~/common/tracking/event-client.tracking'
import { COMMON_EVENT_CLICK_TRACKING } from '~/common/constants/tracking.constant'
import { availablePerdiemShiftStore } from '~/features/available-perdiem-shift/available-perdiem-shift-landing.store'

export function checkArrayItemIncludesAnyElement(array, otherArray) {
	return some(array, (item) => {
		// Efficiently check for common elements using some and includes
		return some(otherArray, (el) => includes(item, el))
	})
}

export const ApplyJobDialog = observer(({ open, setClose }) => {
	const { t } = useTranslation()
	const { workerBasicInfo, fetchWorkerBasicInfo: fetchWorkerBasicInfoData } = careStore
	const { fetchWorkerBasicInfo, fetchDisciplinesSpecialties } = infoTabStore
	const { fetchLookupSkillChecklistsByJob, lookupSkillChecklistsByJob, setShowDialogMatchProfession } = additionalInfoApplyDialogStore
	const { fetchJobs } = availablePerdiemShiftStore

	const { currentJob, currentJobID } = quickApplyStore
	const displineJobId = currentJob.jobGeneral?.specialties.map((item) => item?.discipline?.id) || []
	const { resumes } = careProfileStore
	const { references, fetchWorkerReferences } = referenceTabStore
	const [showError, setShowError] = useState(false)
	const { fetchWorkerResumes } = careProfileStore
	const chipClasses = useClipClasses()
	const [showVerifyPhone, setShowVerifyPhone] = React.useState(false)
	const { getActiveFeature } = careStore
	const mappingBasicInfo = {
		...workerBasicInfo,
		discipline: mapDisciplineSpecialty(workerBasicInfo?.workerDisciplineSpecialties, 'discipline', 'disciplineName'),
		specialty: mapDisciplineSpecialty(workerBasicInfo?.workerDisciplineSpecialties, 'specialty', 'name'),
	}

	useEffect(() => {
		//add promise.all
		Promise.all([fetchWorkerResumes(), fetchWorkerReferences(), fetchWorkerBasicInfo(), fetchWorkerBasicInfoData(), fetchDisciplinesSpecialties()])
		setShowError(false)
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])
	const { code: activeCode } = getActiveFeature() || {}
	const handleOnClose = () => {
		setClose(false)
		logClickEvent(COMMON_EVENT_CLICK_TRACKING.closeBtn, {
			job_id: currentJobID,
			is_account_created: true, // true HERE
		})
		setShowError(false)
	}

	const handleClickOpenUpdate = async () => {
		await setClose(false)
		await careJobDetailStore.setShowApplyUpdateDialog(true)
		await logClickEvent(COMMON_EVENT_CLICK_TRACKING.updateAndApplyBtn, {
			job_id: currentJobID,
			is_account_created: true, // true HERE
		})
	}

	useEffectOnce(() => {
		Promise.all([fetchWorkerBasicInfo(), fetchDisciplinesSpecialties()])
		open && logTrackingScreenEvent(COMMON_EVENT_CLICK_TRACKING.applyForThisJobUpdateConfirmScreen, { is_start_screen_session: true })
	})

	useEffect(() => {
		if (open && !!currentJobID) {
			fetchLookupSkillChecklistsByJob(currentJobID)
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, currentJobID])

	const shouldOpenAdditionalInfo =
		(currentJob?.atsSource === 'symplr' && (careStore.symplrStatus === 'Prospect' || isNil(careStore.symplrStatus))) ||
		(currentJob?.atsSource === 'bullhorn' && (careStore.bullhornStatus === 'New Lead' || isNil(careStore.bullhornStatus)))
	const shouldShowAdditionalApplication = shouldOpenAdditionalInfo && (isEmpty(resumes) || references?.length < 2 || !isEmpty(lookupSkillChecklistsByJob))

	const dataMappingSpecialty = mapDisciplineSpecialty(workerBasicInfo?.workerDisciplineSpecialties, 'discipline', 'disciplineName')
	const workerDisplineIds = dataMappingSpecialty?.map((item) => item.value)

	const isNotDependDispline = checkArrayItemIncludesAnyElement(displineJobId, workerDisplineIds)

	const checkIsLTS = currentJob?.isPerDiemLongTerm !== true && currentJob?.atsSource === 'symplr'
	const renderLabelSubmit = () => {
		if (currentJob?.atsSource === 'symplr') {
			return 'Request'
		} else {
			return 'Confirm'
		}
	}

	const checkSL = currentJob?.atsSource === 'symplr'
	return (
		<>
			<Dialog open={open} onClose={handleOnClose} css={quickApplyModalStyled}>
				<Box m={2}>
					<Grid container className="wrap-title">
						<Grid item xs={12} className="title">
							<Typography>{checkSL ? t('APPLY_CONFIRM_TITLE_SL_NO_LTO') : t('APPLY_CONFIRM_TITLE')}</Typography>
						</Grid>

						<div
							onClick={handleOnClose}
							style={{
								position: 'absolute',
								top: 15,
								right: 15,
								cursor: 'pointer',
							}}
						>
							<SvgIcon component={CloseIconDialog} />
						</div>

						<Grid item xs={12}>
							<Typography
								css={[
									appStore.getTheme.typography.p1,
									{
										color: appStore.getTheme.colors.content.dark,
										margin: '10px 0',
									},
								]}
							>
								{checkSL ? t('APPLY_CONFIRM_DESC_SL_NO_LTO') : t('APPLY_CONFIRM_DESC')}
							</Typography>
						</Grid>
					</Grid>

					<Formik
						enableReinitialize
						initialValues={mappingBasicInfo}
						onSubmit={async (variables, { setErrors }) => {
							if (isEmpty(dataMappingSpecialty)) {
								setShowError(true)
								return
							}

							if (!isNotDependDispline && checkIsLTS) {
								setShowDialogMatchProfession(true)
								return
							}

							if (!isNil(workerBasicInfo?.phone) && !isNil(workerBasicInfo?.phoneVerifiedAt)) {
								if (!shouldShowAdditionalApplication) {
									await careJobDetailStore.actionApplyJob({ ...variables }, { setErrors }, setClose).then((res) => {
										careJobDetailStore.setJobItem(res)
										fetchJobs()
									})

									if (activeCode === 'jobs') {
										await careJobDetailStore.fetchJob(quickApplyStore?.currentJobID)
									}
								} else {
									await additionalInfoApplyDialogStore.changeActiveStep(0)
									await quickApplyStore.setAutoAssignScl(false)
									await quickApplyStore.setShowAdditionInfo(true)
									additionalInfoApplyDialogStore.percentPerStep = {}
								}

								logClickEvent(COMMON_EVENT_CLICK_TRACKING.confirmBtn, {
									job_id: currentJobID,
									is_account_created: true, // true HERE
								})
							} else {
								await setShowVerifyPhone(true)
							}
						}}
					>
						{({ handleSubmit, isSubmitting, values }) => {
							const { specialty, discipline } = values || {}
							const viewValueDiscipline = useMemo(() => {
								const uniqueLabels = uniq(discipline?.map((opt) => opt.label))
								return uniqueLabels.join(', ')
							}, [discipline])

							const viewValueSpecialty = useMemo(() => {
								const uniqueLabels = uniq(specialty?.map((opt) => opt.label))
								return uniqueLabels.join(', ')
							}, [specialty])
							return (
								<Form onSubmit={handleSubmit}>
									<div className="wrap-form">
										<div>
											<Grid container spacing={2}>
												<Grid item xs={6}>
													<TextField
														mode={FIELD_MODE.view}
														className={'firstName'}
														name="firstName"
														placeholder={t('$PLACEHOLDERS.FIRST_NAME')}
														label={t('FIRST_NAME')}
														validate={composeT(t)(required, maxLength(60))}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														mode={FIELD_MODE.view}
														className={'lastName'}
														name="lastName"
														placeholder={t('$PLACEHOLDERS.LAST_NAME')}
														label={t('LAST_NAME')}
														validate={composeT(t)(required, maxLength(60))}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														mode={FIELD_MODE.view}
														className={'emailField'}
														name={`email`}
														label={t('EMAIL_ADDRESS')}
														placeholder={t('$PLACEHOLDERS.EMAIL')}
														validate={composeT(t)(required, email)}
													/>
												</Grid>
												<Grid item xs={6}>
													<PhoneField
														mode={FIELD_MODE.view}
														endLabel={
															<Typography color="textSecondary" variant="p1">
																{t('OPTIONAL')}
															</Typography>
														}
														placeholder={t('$PLACEHOLDERS.PHONE')}
														name={`phone`}
														label={t('PHONE_NUMBER')}
													/>
												</Grid>
												{
													<>
														<Grid item xs={12}>
															<Box>
																<Box mb={1} css={labelStyle}>
																	<label>Professions</label>
																</Box>
																{viewValueDiscipline ? (
																	<Box display="flex" flexWrap={'wrap'} gridGap={8}>
																		{viewValueDiscipline?.split(',').map((item) => (
																			<Chip size="small" classes={chipClasses} label={`${item}`} />
																		))}
																	</Box>
																) : (
																	'-'
																)}
															</Box>
														</Grid>
														{showError && (
															<FormHelperText style={{ color: '#FF2727', fontSize: 13, fontWeight: 600, marginLeft: 5 }}>
																Please select your profession and specialty
															</FormHelperText>
														)}
													</>
												}
												{specialty.length > 0 && (
													<Grid item xs={12}>
														<Box>
															<Box mb={1} css={labelStyle}>
																<label>Specialties</label>
															</Box>
															{viewValueSpecialty ? (
																<Box display="flex" flexWrap={'wrap'} gridGap={8}>
																	{viewValueSpecialty?.split(',').map((item) => (
																		<Chip size="small" classes={chipClasses} label={`${item}`} />
																	))}
																</Box>
															) : (
																'-'
															)}
														</Box>
													</Grid>
												)}
											</Grid>
										</div>
									</div>

									<Grid container spacing={2}>
										<Grid style={{ marginTop: 20, marginBottom: 20 }} item justify="space-between" container>
											<Grid item xs={6} style={{ paddingLeft: 4 }}>
												<Button onClick={handleClickOpenUpdate} variant="outlined" type="button">
													Update
												</Button>
											</Grid>
											<Grid item xs={6} style={{ paddingLeft: 4 }}>
												<Button disabled={isSubmitting} loading={isSubmitting} type="submit">
													{renderLabelSubmit()}
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Form>
							)
						}}
					</Formik>
				</Box>
			</Dialog>
			<UpdateApplyJobDialog />
			<NotifyDialog
				eventName={COMMON_EVENT_CLICK_TRACKING.applyForThisJobVerifyPhoneNumScreen}
				onClose={() => {
					setShowVerifyPhone(false)
				}}
				showButtonClose={true}
				okText="Verify now"
				open={showVerifyPhone}
				onOk={async () => {
					await setShowVerifyPhone(false)
					await handleClickOpenUpdate()
				}}
				children={'Please verify your phone number to continue. Thank you for your cooperation.'}
				title={
					currentJob?.atsSource === 'symplr'
						? ' Phone number verification is required to proceed with your shift request'
						: 'Phone number verification is required to proceed with your job application'
				}
				onCancel={() => {
					setShowVerifyPhone(false)
				}}
			/>
		</>
	)
})
